<template>
  <section class="container boxWithBorder" style="border-color: #a4393a;">
    <b-message
      title="Ticket Exchange General Restrictions"
      aria-close-label="Close message"
      type="is-warning"
      :closable="false"
    >
      <ul>
        <li>
          All ticket exchanges must be made before departure of the original flight (no exchanges of “partially flown” tickets).
        </li>
        <li>
          Exchanges must be for round-trip/one-way travel only.
        </li>
        <li>
          Flights originally booked as NDC fares are not eligible for exchanges through TripCircuit.
        </li>
        <li>
          Changes are limited to change of schedule (dates/times), origin/destination, and airline (if applicable).
        </li>
        <li>
          Ticket exchanges apply to flight tickets only. Please note that any ancillary services (e.g. upgrades, seat purchases) on the orginal booking are not included in the exchange and may be lost.
        </li>
        <li>
          Additional restrictions may be determined by the airline on a per ticket basis.
        </li>
      </ul>
    </b-message>

    <div v-if="loaded">
      <div class="message is-success" v-if="ticketRules.general == 'Y'">
        <div class="message-body">
          <div class="is-size-5 mb-2 has-text-weight-bold">
            Good news! This ticket is eligible for an exchange.
          </div>
          <div class="mb-2">Additional rules apply to this ticket:</div>

          <ul>
            <li>
              The new flight must be booked on the following airlines only:
              {{
                ticketRules.airlines
                  .map((airline) => {
                    return $helpers.getAirlineName(airline);
                  })
                  .join(", ")
              }}.
            </li>
            <li v-if="ticketRules.penalty == 'Y'">
              An exchange fee may apply.
            </li>

            <li v-if="ticketRules.geo != 'Y'">
              Departure/Arrival location for the new flights must be the same as
              original ticket.
            </li>
          </ul>
        </div>
      </div>

      <div v-else class="message is-danger">
        <div class="message-body">
          <div class="is-size-5 mb-2 has-text-weight-bold">
            Sorry, this ticket is not eligible to be exchanged online at this time. If you are in need of reservation support,
please contact us at 877-924-0303.
          </div>
          <div class="has-text-weight-semibold is-size-6" v-if="reason"> Additional Details: </div>
          <li class="has-text-weight-normal is-size-6" v-if="reason"> {{ reason }}</li>
        </div>
      </div>
    </div>

    <b-button
      class="button mt-2 mb-5"
      @click="cancelExchange"
      icon-right="window-close"
    >
      Cancel
    </b-button>

    <button
      class="button is-primary mt-2 mb-5 ml-4"
      @click="ticketCheck"
      v-if="ticketRules.general == null"
    >
      Check eligibility
    </button>

    <b-button
      class="button is-primary mt-2 mb-5 ml-4"
      icon-right="arrow-right"
      @click="pushToSearch"
      v-if="ticketRules.general == 'Y'"
    >
      Continue
      <b-loading
        :is-full-page="false"
        v-model="pending"
        :can-cancel="true"
      ></b-loading>
    </b-button>

    <b-loading
      :is-full-page="false"
      v-model="pending"
      :can-cancel="true"
    ></b-loading>
  </section>
</template>

<script>
import moment from "moment";

export default {
  name: "AtcTicketCheck",
  props: {
    trip: {
      type: Object,
      required: true,
    },
    tst: {
      type: Object,
      required: true,
    },
  },
  components: {},
  data: function() {
    return {
      loaded: false,
      pending: false,
      reason: "None.",
      ticketRules: {
        general: null,
        geo: null,
        penalty: null,
        airlines: [],
      },
    };
  },
  computed: {
    ticketNumber() {
      return this.tst.ticketNumber.replace(/\D/g, "").substring(0, 13);
    },
    isPastDate() {
      let startDate = new Date(this.trip.startDate);
      let today = new Date();
      return startDate < today;
    },
    isVoidable() {
      let now = moment();
      let ticketExpirationDate = moment(this.trip.ticketElement.ticket.date, "DDMMYY").add(1, "days");

      if (ticketExpirationDate.isAfter(now)) return true;
      else return false;
    },
  },
  methods: {
    //Internal TripCircuit rules
    preCheck() {
      if (this.trip.isNdcBooking){ //NDC not supported with standard ATC 
        this.reason = "NDC tickets not eligible.";
        return false;
      } else if (this.isPastDate){ //disable past dated or "partially flown" tickets for now
      this.reason = "Origin flight has departed.";
        return false;
      } else if (this.tst.journeys.length > 2){ //no multi-city for now
        this.reason = "Flight has more than two legs.";
        return false;
      } else if (this.trip.tickets.length > 1){ //no split ticket or guest tickets on pnr
        this.reason = "Multiple tickets on the same reservation.";
        return false;
      } else if (this.isVoidable){ //no split ticket or guest tickets on pnr
        this.reason = "Ticket is still in void window. Please cancel this ticket for a full refund instead of performing an exchange.";
        return false;
        
      }
      return true;
    },
    async ticketCheck() {
      this.pending = true;
      if (!this.preCheck()) {
        this.pending = false;
        this.loaded = true;
        this.ticketRules.general = "N";
        return;
      }
      let response = await this.$app.huddleLabsServerCall(
        "amadeus/air/check-atc-eligibility",
        {
          ticketNumbers: Array(this.ticketNumber),
        }
      );

      if (response.data) {
        this.ticketRules.general = response.data.ticketRules?.general;
        this.ticketRules.geo = response.data.ticketRules?.geo;
        //this.ticketRules.date = response.data.ticketRules?.date;
        this.ticketRules.penalty = response.data.ticketRules?.penalty;

        if (response.data.allowedCarriers == "$") {
          this.ticketRules.airlines = []; // All airlines
        } else {
          this.ticketRules.airlines = response.data.allowedCarriers;
        }

      }

      this.loaded = true;
      this.pending = false;
    },
    async displayDoc() {
      this.pending = true;
      let response = await this.$app.huddleLabsServerCall(
        "amadeus/air/display-document",
        {
          ticketNumber: this.ticketNumber,
        }
      );
      this.pending = false;
    },
    cancelExchange() {
      this.$store.dispatch("bookTravel/setAtcDetails", {});
      this.$parent.close();
      //this.$router.push({ name: "planned-trips" });
    },
    pushToSearch() {
      //set default search status to "C" = "Change"
      //let segments = this.tst.segments.map((segment) => {
      //  return segment;
      //});

      let origin = this.tst.segments[0].origin;
      let destination = this.trip.airSummary.destinationAirport.code;

      let atcDetails = {
        useAtcShopper: true,
        ticketNumber: this.tst.ticketNumber.replace(/\D/g, "").substring(0, 13),
        airline: this.trip.ticketElement.ticket.airlineCode,
        segments: this.tst.segments,
        journeys: this.tst.journeys,
        origin: origin,
        destination: destination,
        startDate: this.trip.startDate,
        ticketRules: this.ticketRules,
        endDate: this.trip.endDate,
        price: this.tst.fare.total,
      };

      this.$store.dispatch("bookTravel/clearBooking");
      this.$store.dispatch("bookTravel/setTripResult", this.trip);
      this.$store.dispatch("bookTravel/setAtcDetails", atcDetails);

      this.$router.push({
        name: "BookingFlights",
      });
    },
  },
};
</script>

<style scoped></style>
